import { useEffect } from "react";
import { useAppSelector } from "@/store/hooks";
import { decodeJwt } from "./jwt";
import { refreshAccessToken } from "@/api/privateClient";

/**
 * Silently renews the access token a couple minutes before it expires, as
 * long as a session is live. Access tokens are short-lived on purpose (15m
 * — see backend/src/modules/auth/services/authService.js
 * ACCESS_TOKEN_EXPIRY) and meant to be renewed via the 30-day refresh-token
 * cookie; without this, nothing renews the token until an API call happens
 * to 401 right after expiry. In the meantime, ProtectedRoute/PublicRoute's
 * client-side isTokenExpired checks (see routes/ProtectedRoute.tsx) run on
 * every in-app navigation and would treat the merely-stale-but-renewable
 * token as a dead session, hard-logging the user out on literally the next
 * click after 15 minutes. This closes that gap; ProtectedRoute's check
 * remains as the correct last-resort (e.g. laptop asleep past the
 * refresh-token's own lifetime).
 */
export function SessionKeepAlive() {
  const token = useAppSelector((state) => state.auth.token);

  useEffect(() => {
    if (!token) return;
    const payload = decodeJwt(token);
    if (!payload?.exp) return;

    const REFRESH_BUFFER_MS = 2 * 60 * 1000;
    const msUntilRefresh = Math.max(payload.exp * 1000 - Date.now() - REFRESH_BUFFER_MS, 5000);

    const timer = setTimeout(() => {
      refreshAccessToken().catch(() => {
        // Refresh token itself is expired/revoked (e.g. the device was
        // asleep for a long stretch, or was logged out elsewhere) — leave
        // it to the next API call's 401, or the next navigation's
        // ProtectedRoute check, to send the user back to /login.
      });
    }, msUntilRefresh);

    const onVisible = () => {
      if (document.visibilityState === "visible" && payload.exp * 1000 - Date.now() < REFRESH_BUFFER_MS) {
        refreshAccessToken().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // Re-runs whenever `token` changes — including after this same effect's
    // own refresh updates it via sessionSync.ts — so it keeps re-arming
    // itself against the newest token's expiry, not the original one.
  }, [token]);

  return null;
}
